import { apiClient } from "@/api/client";
import { create } from "zustand";

interface CampaignHeader {
  id: string;
  name: string;
  status: string;
  progress_status: string;
  project_code: string;
}

interface AdTypeHeader {
  id: string;
  name: string;
  media_type: string;
  media_type_id: string;
}

interface PublisherHeader {
  id: string;
  name: string;
  type: string;
}

interface AdCategoryHeader {
  id: string;
  name: string;
}

interface Headers {
  campaign: CampaignHeader;
  ad_type: AdTypeHeader;
  publisher: PublisherHeader;
  ad_category: AdCategoryHeader;
  ad_category_mapping_id: string;
}

interface CanvasInfoAggregated {
  status: string;
  canvasWidth: number;
  canvasHeight: number;
  selectedAdSpecs: string[];
  selectedInterface: string;
  selectedAdInfoBlocks: string[];
}

interface CanvasMaster {
  id: string;
  ad_category_mapping_id: string;
  status: string;
  canvas_info_aggregated: CanvasInfoAggregated;
  version: number;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

interface LocationJson {
  x: number;
  y: number;
  width: number;
  height: number;
  z_index: number;
}

interface ComponentLocation {
  id: string;
  interface_id: string;
  location_json: LocationJson;
  version: number;
}

interface TextSettings {
  // API-spec keys (stored in aggregated_json, returned from server)
  fontSize?: number;
  fontFamily?: string | null;
  fontWeight?: string;
  fontStyle?: string;
  textDecoration?: string;
  color?: string;
  textAlign?: string;
  lineHeight?: number;
  letterSpacing?: number;
  textTransform?: string;
  opacity?: number;
}

interface ShapeSettings {
  fill?: string;
  opacity?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
}

interface ButtonSettings {
  text?: string;
  backgroundColor?: string;
  textColor?: string;
  fontSize?: number;
  fontWeight?: string;
  borderRadius?: number;
  paddingX?: number;
  paddingY?: number;
}

interface AggregatedJson {
  label: string;
  content: string | null;
  textSettings?: TextSettings;
  shapeSettings?: ShapeSettings;
  buttonSettings?: ButtonSettings;
}

interface CanvasComponent {
  id: string;
  canvas_component_master_id: string;
  canvas_component_master_name: string;
  canvas_component_master_display_name: string;
  canvas_component_sub_master_id: string;
  canvas_component_sub_master_name: string;
  canvas_component_sub_master_display_name: string;
  component_category: string;
  aggregated_json: AggregatedJson;
  version: number;
  locations: Record<string, ComponentLocation>;
}

interface Canvas {
  canvas_master: CanvasMaster;
  components: CanvasComponent[];
  ad_info: any[];
}

interface TextDefaults {
  color: string;
  font_size: number;
  font_weight: string;
}

interface ContentSettings {
  label: string;
  input_type: string;
  placeholder: string;
  text_defaults: TextDefaults;
  show_on_canvas: boolean;
  character_limit: number;
}

interface ContentObjectiveItem {
  canvas_ad_info_id: string | null;
  sub_master_id: string;
  name: string;
  display_name: string;
  settings: ContentSettings;
  saved_value: string | null;
}

interface ContentObjective {
  content: ContentObjectiveItem[];
  objective: ContentObjectiveItem[];
  media: boolean;
}

interface MediaSlot {
  slot_index: number;
  type: string;
  status: string;
  url: string | null;
  original_name: string | null;
  canvas_component_info_id: string;
}

interface Media {
  slots: MediaSlot[];
  carousel_count: number;
  media_component_type: string;
  has_media: boolean;
}

interface AdSpecOverview {
  id: string;
  interface_id: string;
  ad_spec_overview: string[];
  ad_spec_values?: Record<string, any>;
  ad_info_blocks_overview: string[];
  version: number;
}

/**
 * An ad-category parameter flagged as restricted.
 *
 * Always present on the response, but empty unless the user holds
 * `read_restricted_parameters` — so no permission check is needed here: an
 * empty list simply renders nothing.
 */
/**
 * A single decoded field inside a restricted parameter.
 *
 * Most restricted parameters are `input_type: "group"`, so the group-level
 * input_type says nothing useful about how to render them — the real per-field
 * detail only exists here. The backend builds these with the same helper the
 * Assets page uses, so both surfaces derive controls from the same shape.
 *
 * For a "Multiple" parameter, `value` holds the configured list of allowed
 * values (which is what the dropdown is built from), not a current selection.
 */
export interface RestrictedParameterField {
  parameter_field_id?: string | null;
  key?: string | null;
  label?: string;
  /** e.g. "text" | "number" | "date" | "select" | "list|text". */
  type?: string;
  options?: { title: string; value: string }[] | string[] | null;
  /** The ad-category configuration. For "Multiple", the allowed value list. */
  value?: unknown;
  /**
   * This version's own answer, or null if nothing has been chosen yet. Kept
   * separate from `value` so the UI can show the configured options and the
   * current selection at the same time.
   */
  saved_value?: unknown;
}

export interface RestrictedParameter {
  id: string;
  value_type: string;
  is_enabled: boolean;
  /** Definition key, mirrored from the block builder. */
  key?: string;
  label?: string;
  /** Decoded per-field specs. Prefer this over the raw *_json columns. */
  fields?: RestrictedParameterField[];
  ui_schema?: Record<string, any> | null;
  fields_json?: Record<string, any> | any[] | null;
  inputs_json?: Record<string, any> | any[] | null;
  value?: Record<string, any> | any[] | null;
  visibility_metadata?: { restricted?: boolean; surface?: string } | null;
  parameter_definition?: {
    id: string;
    key: string;
    label: string;
    input_type: string;
    options?: { title: string; value: string }[] | null;
    is_required?: boolean;
    is_multiple?: boolean;
  } | null;
}

export interface AdBuilderData {
  headers: Headers;
  canvas: Canvas;
  content_objective: ContentObjective;
  media: Media;
  ad_spec_overview: AdSpecOverview[];
  restricted_parameters?: RestrictedParameter[];
  asset_status: string;
}

export interface StatusTransition {
  status: string;
  label: string;
  action: string;
}

export interface AssetVersion {
  id: string;
  assets_row_id: string;
  version_number: number;
  ad_title: string | null;
  status: string;
  content_json: Record<string, any> | null;
  objective_json: Record<string, any> | null;
  media_json: Record<string, any> | null;
  available_transitions?: StatusTransition[];
}

export interface ChangelogEntry {
  id: string;
  action: string;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  comment_id: string | null;
  changed_by: string;
  changed_by_user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture: string | null;
  } | null;
  created_at: string;
}

/** One per-version parameter value the Ad Builder is saving. */
export interface RestrictedParameterValuePayload {
  ad_category_parameter_value_id: string;
  parameter_definition_id?: string | null;
  parameter_key: string;
  field_key?: string | null;
  /** An empty value clears the stored selection for this version. */
  value: string | null;
}

interface UpdateVersionPayload {
  ad_title?: string;
  content_json?: Record<string, any>;
  objective_json?: Record<string, any>;
  media_json?: Record<string, any>;
  media_files?: Record<string, File>;
  restricted_parameters?: RestrictedParameterValuePayload[];
}

interface TransitionStatusPayload {
  status: string;
  comment?: string;
}

interface AdBuilderDataStore {
  adBuilderData: AdBuilderData | null;
  isLoading: boolean;
  isSaving: boolean;
  isTransitioning: boolean;
  error: string | null;
  assetVersions: AssetVersion[];
  activeVersionId: string | null;
  changelog: ChangelogEntry[];
  isChangelogLoading: boolean;
  /**
   * `versionId` scopes the restricted parameter values, which are saved per
   * version. Omitting it returns the ad-category configuration with no saved
   * selections.
   */
  fetchAdBuilderData: (
    campaignId: string,
    adTypeId: string,
    assetsRowId?: string | null,
    versionId?: string | null,
  ) => Promise<void>;
  fetchAssetVersions: (assetsRowId: string) => Promise<void>;
  fetchChangelog: (versionId: string) => Promise<void>;
  fetchAvailableTransitions: (versionId: string) => Promise<StatusTransition[]>;
  updateAssetVersion: (
    versionId: string,
    payload: UpdateVersionPayload,
  ) => Promise<void>;
  transitionStatus: (
    versionId: string,
    payload: TransitionStatusPayload,
  ) => Promise<void>;
  createAssetVersion: (assetsRowId: string) => Promise<void>;
  deleteAssetVersion: (versionId: string) => Promise<void>;
  downloadAssetVersionMedia: (versionId: string) => Promise<void>;
  setActiveVersionId: (id: string) => void;
  reset: () => void;
}

const useAdBuilderDataStore = create<AdBuilderDataStore>((set) => ({
  adBuilderData: null,
  isLoading: false,
  isSaving: false,
  isTransitioning: false,
  error: null,
  assetVersions: [],
  activeVersionId: null,
  changelog: [],
  isChangelogLoading: false,

  fetchAdBuilderData: async (
    campaignId: string,
    adTypeId: string,
    assetsRowId?: string | null,
    versionId?: string | null,
  ) => {
    set({ isLoading: true, error: null });
    try {
      const query = new URLSearchParams();
      if (assetsRowId) query.set("assetsRowId", assetsRowId);
      if (versionId) query.set("versionId", versionId);

      const suffix = query.toString() ? `?${query.toString()}` : "";
      const url = `/campaign/${campaignId}/ad-builder/${adTypeId}${suffix}`;
      const response = await apiClient.get(url);
      set({ adBuilderData: response.data.data, isLoading: false });
    } catch (error: any) {
      set({
        error:
          error?.response?.data?.message ?? "Failed to load ad builder data",
        isLoading: false,
      });
    }
  },

  fetchAssetVersions: async (assetsRowId: string) => {
    try {
      const response = await apiClient.get(`/asset-versions/${assetsRowId}`);
      const versions: AssetVersion[] = response.data.data ?? [];
      set({ assetVersions: versions });
    } catch {
      set({ assetVersions: [] });
    }
  },

  downloadAssetVersionMedia: async (versionId: string) => {
    // Streams a single file or a ZIP; the server names it per the campaign's
    // file-naming convention (Content-Disposition), so we honour that when set.
    let response;
    try {
      response = await apiClient.get(`/asset-versions/${versionId}/download`, {
        responseType: "blob",
      });
    } catch (error: any) {
      // Error bodies arrive as a Blob (responseType: blob) — parse the JSON
      // message so the caller can show a useful toast.
      const blob = error?.response?.data;
      let message = "Failed to download asset media.";
      if (blob instanceof Blob) {
        try {
          const parsed = JSON.parse(await blob.text());
          message = parsed?.message ?? message;
        } catch {
          /* keep default */
        }
      }
      throw new Error(message);
    }

    const disposition = response.headers["content-disposition"] ?? "";
    const match = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(disposition);
    let fileName = match
      ? decodeURIComponent(match[1])
      : `asset_${versionId}.zip`;

    // The endpoint always returns a ZIP archive. Guarantee the saved file keeps
    // the .zip extension even if the Content-Disposition header is missing or
    // stripped (e.g. by a proxy), so the browser never falls back to .txt.
    if (!/\.zip$/i.test(fileName)) {
      fileName = `${fileName}.zip`;
    }

    // Force the correct MIME type so the blob is treated as a ZIP, not text.
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: "application/zip" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  fetchChangelog: async (versionId: string) => {
    set({ isChangelogLoading: true });
    try {
      const response = await apiClient.get(
        `/asset-versions/${versionId}/changelog`,
      );
      set({ changelog: response.data.data ?? [], isChangelogLoading: false });
    } catch {
      set({ changelog: [], isChangelogLoading: false });
    }
  },

  fetchAvailableTransitions: async (versionId: string) => {
    try {
      const response = await apiClient.get(
        `/asset-versions/${versionId}/available-transitions`,
      );
      return response.data.data.transitions ?? [];
    } catch {
      return [];
    }
  },

  updateAssetVersion: async (
    versionId: string,
    payload: UpdateVersionPayload,
  ) => {
    set({ isSaving: true });
    try {
      const { media_files, ...fields } = payload;
      let response: { data: { data: AssetVersion } };
      if (media_files && Object.keys(media_files).length > 0) {
        const formData = new FormData();
        if (fields.ad_title !== undefined)
          formData.append("ad_title", fields.ad_title);
        if (fields.content_json)
          formData.append("content_json", JSON.stringify(fields.content_json));
        if (fields.objective_json)
          formData.append(
            "objective_json",
            JSON.stringify(fields.objective_json),
          );
        if (fields.media_json)
          formData.append("media_json", JSON.stringify(fields.media_json));
        // Sent as JSON because multipart cannot express a nested array; the
        // request decodes it in prepareForValidation().
        if (fields.restricted_parameters)
          formData.append(
            "restricted_parameters",
            JSON.stringify(fields.restricted_parameters),
          );
        Object.entries(media_files).forEach(([slot, file]) => {
          formData.append(`media_files[${slot}]`, file);
        });
        response = await apiClient.post(
          `/asset-versions/${versionId}`,
          formData,
        );
      } else {
        response = await apiClient.put(`/asset-versions/${versionId}`, fields);
      }
      const updated: AssetVersion = response.data.data;
      set((state) => ({
        isSaving: false,
        assetVersions: state.assetVersions.map((v) =>
          v.id === versionId ? { ...v, ...updated } : v,
        ),
      }));
    } catch (error) {
      set({ isSaving: false });
      // Surface a validation reason (e.g. text over the template's character
      // limit) instead of a generic failure.
      const data = (error as any)?.response?.data;
      const firstError = data?.errors
        ? (Object.values(data.errors).flat()[0] as string | undefined)
        : undefined;
      throw new Error(firstError || data?.message || "Failed to save version");
    }
  },

  transitionStatus: async (
    versionId: string,
    payload: TransitionStatusPayload,
  ) => {
    set({ isTransitioning: true });
    try {
      const response = await apiClient.post(
        `/asset-versions/${versionId}/transition`,
        payload,
      );
      const updated: AssetVersion = response.data.data;
      set((state) => ({
        isTransitioning: false,
        assetVersions: state.assetVersions.map((v) =>
          v.id === versionId ? { ...v, ...updated } : v,
        ),
      }));
    } catch {
      set({ isTransitioning: false });
      throw new Error("Failed to transition status");
    }
  },

  createAssetVersion: async (assetsRowId: string) => {
    const response = await apiClient.post(`/asset-versions/quick-create`, {
      assets_row_id: assetsRowId,
    });
    const newVersion: AssetVersion = response.data.data;
    set((state) => ({ assetVersions: [...state.assetVersions, newVersion] }));
  },

  deleteAssetVersion: async (versionId: string) => {
    await apiClient.delete(`/asset-versions/quick-delete/${versionId}`);
    set((state) => ({
      assetVersions: state.assetVersions.filter((v) => v.id !== versionId),
      activeVersionId:
        state.activeVersionId === versionId ? null : state.activeVersionId,
    }));
  },

  setActiveVersionId: (id: string) => set({ activeVersionId: id }),

  reset: () =>
    set({
      adBuilderData: null,
      isLoading: false,
      isSaving: false,
      isTransitioning: false,
      error: null,
      assetVersions: [],
      activeVersionId: null,
      changelog: [],
      isChangelogLoading: false,
    }),
}));

export default useAdBuilderDataStore;
